<template>
    <div class="cal-wrap">
        <div class="cal-header">
            <button type="button" class="cal-nav" :disabled="disabled" @click="shiftMonth(-1)">‹</button>
            <div class="cal-title">{{ year }}年{{ month + 1 }}月</div>
            <button type="button" class="cal-nav" :disabled="disabled" @click="shiftMonth(1)">›</button>
        </div>
        <div class="cal-weekdays">
            <span v-for="w in weekLabels" :key="w">{{ w }}</span>
        </div>
        <div class="cal-days" :class="{ 'is-dragging': drag.active && drag.moved }"
            @mousedown="onMouseDown">
            <button type="button" class="cal-day" v-for="(cell, index) in cells" :key="index"
                :class="dayClass(cell)" :data-date="cell.date || ''" :disabled="disabled || cell.empty"
                tabindex="-1">{{ cell.empty ? '' : cell.day }}</button>
        </div>
        <div class="cal-footer">
            <div class="cal-footer-hint">{{ hint }}</div>
            <div class="cal-footer-actions">
                <slot name="actions">
                    <el-button size="mini" :disabled="disabled" @click="clear">清空</el-button>
                </slot>
            </div>
        </div>
    </div>
</template>

<script>
    import { toDateKey, todayKey, getRangeKeys, WEEK_LABELS } from '../log-moment-shared.js';

    const DRAG_THRESHOLD = 5;

    export default {
        name: 'DateMultiCalendar',
        props: {
            value: {
                type: Array,
                default: () => [],
            },
            disabled: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            let now = new Date();
            return {
                year: now.getFullYear(),
                month: now.getMonth(),
                weekLabels: WEEK_LABELS,
                drag: {
                    active: false,
                    moved: false,
                    mode: 'add',
                    start: null,
                    hover: null,
                    startX: 0,
                    startY: 0,
                },
            };
        },
        computed: {
            selectedMap() {
                let map = {};
                (this.value || []).forEach((dk) => { map[dk] = true; });
                return map;
            },
            dragRangeKeys() {
                if (!this.drag.active || !this.drag.start) return [];
                return getRangeKeys(this.drag.start, this.drag.hover || this.drag.start);
            },
            previewAddMap() {
                let map = { ...this.selectedMap };
                if (this.drag.active && this.drag.moved && this.drag.mode === 'add') {
                    this.dragRangeKeys.forEach((k) => { map[k] = true; });
                }
                return map;
            },
            previewRemoveMap() {
                let map = {};
                if (this.drag.active && this.drag.moved && this.drag.mode === 'remove') {
                    this.dragRangeKeys.forEach((k) => { map[k] = true; });
                }
                return map;
            },
            cells() {
                let y = this.year;
                let m = this.month;
                let firstDow = new Date(y, m, 1).getDay();
                let daysInMonth = new Date(y, m + 1, 0).getDate();
                let list = [];
                for (let i = 0; i < firstDow; i++) {
                    list.push({ empty: true, date: '', day: '' });
                }
                for (let d = 1; d <= daysInMonth; d++) {
                    list.push({ empty: false, date: toDateKey(y, m, d), day: d });
                }
                return list;
            },
            hint() {
                if (this.drag.active && this.drag.moved) {
                    let n = this.dragRangeKeys.length;
                    if (this.drag.mode === 'remove') {
                        return `拖取消中… 将取消 ${n} 天，松开完成`;
                    }
                    return `拖选中… 将追加选中，松开完成（原有选中保留）`;
                }
                let count = (this.value || []).length;
                if (!count) return '单击或拖选日期；可跨月累加，原有选中不会取消';
                return `已选 ${count} 天 · 单击切换选中，拖选追加，从已选日期拖动可连取消`;
            },
        },
        mounted() {
            this._onMove = (e) => { if (this.drag.active) this.onMouseMove(e); };
            this._onUp = () => { if (this.drag.active) this.onMouseUp(); };
            document.addEventListener('mousemove', this._onMove);
            document.addEventListener('mouseup', this._onUp);
            this.jumpToSelected();
        },
        beforeDestroy() {
            document.removeEventListener('mousemove', this._onMove);
            document.removeEventListener('mouseup', this._onUp);
        },
        methods: {
            jumpToSelected() {
                let list = (this.value || []).slice().sort();
                if (!list.length) return;
                let p = list[0].split('-').map(Number);
                if (p.length >= 2) {
                    this.year = p[0];
                    this.month = p[1] - 1;
                }
            },
            dayClass(cell) {
                if (cell.empty) return { 'is-empty': true };
                let key = cell.date;
                let cls = {};
                if (key === todayKey()) cls['is-today'] = true;

                if (this.previewRemoveMap[key]) {
                    cls['is-remove-preview'] = true;
                    return cls;
                }

                let map = this.previewAddMap;
                if (!map[key]) return cls;

                let keys = Object.keys(map).sort();
                let prev = null;
                let next = null;
                let idx = keys.indexOf(key);
                if (idx >= 0) {
                    prev = keys[idx - 1];
                    next = keys[idx + 1];
                }
                let hasPrev = prev && this.isNextDay(prev, key);
                let hasNext = next && this.isNextDay(key, next);
                if (!hasPrev && !hasNext) {
                    cls['is-selected'] = true;
                    cls['is-range-start'] = true;
                    cls['is-range-end'] = true;
                } else if (!hasPrev && hasNext) {
                    cls['is-in-range'] = true;
                    cls['is-range-start'] = true;
                } else if (hasPrev && !hasNext) {
                    cls['is-in-range'] = true;
                    cls['is-range-end'] = true;
                } else {
                    cls['is-in-range'] = true;
                }
                if (this.drag.active && this.drag.moved && this.drag.mode === 'add') {
                    cls['is-preview'] = true;
                }
                return cls;
            },
            isNextDay(a, b) {
                let pa = a.split('-').map(Number);
                let da = new Date(pa[0], pa[1] - 1, pa[2] + 1);
                let expect = toDateKey(da.getFullYear(), da.getMonth(), da.getDate());
                return expect === b;
            },
            shiftMonth(delta) {
                if (this.disabled || this.drag.active) return;
                this.month += delta;
                if (this.month > 11) {
                    this.month = 0;
                    this.year += 1;
                } else if (this.month < 0) {
                    this.month = 11;
                    this.year -= 1;
                }
            },
            dateFromPoint(e) {
                let el = document.elementFromPoint(e.clientX, e.clientY);
                let btn = el && el.closest && el.closest('.cal-day[data-date]');
                if (!btn || btn.disabled) return null;
                return btn.getAttribute('data-date') || null;
            },
            onMouseDown(e) {
                if (this.disabled || e.button !== 0) return;
                let btn = e.target.closest && e.target.closest('.cal-day[data-date]');
                if (!btn || !btn.getAttribute('data-date') || btn.disabled) return;
                let key = btn.getAttribute('data-date');
                this.drag.active = true;
                this.drag.moved = false;
                this.drag.start = key;
                this.drag.hover = key;
                this.drag.startX = e.clientX;
                this.drag.startY = e.clientY;
                this.drag.mode = this.selectedMap[key] ? 'remove' : 'add';
            },
            onMouseMove(e) {
                if (!this.drag.active) return;
                let dx = Math.abs(e.clientX - this.drag.startX);
                let dy = Math.abs(e.clientY - this.drag.startY);
                if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD) {
                    this.drag.moved = true;
                }
                let key = this.dateFromPoint(e);
                if (key) this.drag.hover = key;
            },
            onMouseUp() {
                if (!this.drag.active) return;
                let start = this.drag.start;
                let hover = this.drag.hover || start;
                let moved = this.drag.moved;
                let mode = this.drag.mode;
                this.drag.active = false;
                this.drag.moved = false;
                this.drag.start = null;
                this.drag.hover = null;
                this.drag.mode = 'add';
                if (!start) return;

                let next = (this.value || []).slice();
                if (!moved) {
                    let idx = next.indexOf(start);
                    if (idx >= 0) next.splice(idx, 1);
                    else next.push(start);
                } else if (mode === 'remove') {
                    let removeSet = {};
                    getRangeKeys(start, hover).forEach((dk) => { removeSet[dk] = true; });
                    next = next.filter((dk) => !removeSet[dk]);
                } else {
                    let map = {};
                    next.forEach((dk) => { map[dk] = true; });
                    getRangeKeys(start, hover).forEach((dk) => { map[dk] = true; });
                    next = Object.keys(map);
                }
                this.$emit('input', next.sort());
            },
            clear() {
                if (this.disabled) return;
                this.$emit('input', []);
            },
        },
        watch: {
            value(val) {
                if (this.drag.active) return;
                if (val && val.length && !this._initedJump) {
                    this._initedJump = true;
                    this.jumpToSelected();
                }
            },
        },
    };
</script>

<style lang="less" scoped>
    .cal-wrap {
        border: 1px solid #EBEEF5;
        border-radius: 8px;
        overflow: hidden;
        background: #fff;
        user-select: none;
    }
    .cal-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 10px 12px;
        border-bottom: 1px solid #EBEEF5;
    }
    .cal-title {
        font-size: 14px;
        font-weight: 600;
        color: #303133;
    }
    .cal-nav {
        width: 28px;
        height: 28px;
        border: none;
        border-radius: 6px;
        background: #F5F7FA;
        cursor: pointer;
        font-size: 16px;
        color: #606266;
        font-family: inherit;
        &:hover:not(:disabled) { background: #ECF5FF; color: #409EFF; }
        &:disabled { cursor: not-allowed; opacity: 0.5; }
    }
    .cal-weekdays {
        display: grid;
        grid-template-columns: repeat(7, 1fr);
        padding: 8px 12px 0;
        font-size: 12px;
        color: #909399;
        text-align: center;
    }
    .cal-days {
        display: grid;
        grid-template-columns: repeat(7, 1fr);
        gap: 4px;
        padding: 6px 12px 14px;
        &.is-dragging,
        &.is-dragging .cal-day { cursor: grabbing; }
    }
    .cal-day {
        min-height: 48px;
        border: none;
        border-radius: 8px;
        background: transparent;
        cursor: pointer;
        font-family: inherit;
        font-size: 14px;
        color: #303133;
        display: flex;
        align-items: center;
        justify-content: center;
        &:hover:not(:disabled):not(.is-empty) {
            background: #ECF5FF;
            color: #409EFF;
        }
        &.is-empty {
            cursor: default;
            visibility: hidden;
        }
        &.is-today { font-weight: 700; }
        &.is-in-range,
        &.is-range-start,
        &.is-range-end,
        &.is-selected,
        &.is-preview {
            background: #409EFF;
            color: #fff;
            font-weight: 600;
            border-radius: 0;
        }
        &.is-range-start { border-radius: 8px 0 0 8px; }
        &.is-range-end { border-radius: 0 8px 8px 0; }
        &.is-range-start.is-range-end,
        &.is-selected:not(.is-in-range) { border-radius: 8px; }
        &.is-remove-preview {
            background: #F2F3F5;
            color: #909399;
            text-decoration: line-through;
            border-radius: 8px;
            font-weight: 500;
        }
        &.is-today:not(.is-in-range):not(.is-selected):not(.is-range-start):not(.is-range-end):not(.is-remove-preview) {
            box-shadow: inset 0 0 0 1px #409EFF;
        }
        &:disabled:not(.is-empty) { cursor: not-allowed; opacity: 0.7; }
    }
    .cal-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        padding: 10px 12px;
        border-top: 1px solid #EBEEF5;
        background: #fafafa;
        flex-wrap: wrap;
    }
    .cal-footer-hint {
        font-size: 12px;
        color: #909399;
        flex: 1;
        min-width: 160px;
    }
    .cal-footer-actions {
        display: flex;
        gap: 8px;
        flex-shrink: 0;
    }
</style>
